import { useMe } from '@/features/auth/hooks'
import type { Me } from '@/features/auth/types'
import type { ModuleNavItem } from '@/lib/navigation'

/**
 * Permission gating — central authorization layer.
 *
 * =============================================================================
 * ARCHITECTURE NOTE (read before adding logic here)
 * =============================================================================
 *
 * The backend is the source of truth. This module is a CLIENT-SIDE MIRROR of
 * the backend's permission model. It is used to:
 *   1. Hide nav items the user cannot access (Sidebar, ModuleTopNav).
 *   2. Show 403 route guards before the backend returns an error.
 *   3. Enable/disable affordances inside pages (e.g. "Approve" button).
 *
 * CLIENT-SIDE CHECKS ARE NOT A SECURITY BOUNDARY. The backend re-checks on
 * every request. Never rely solely on frontend gating.
 *
 * =============================================================================
 * SCOPE MODEL (ADR-0024)
 * =============================================================================
 *
 * There are two scopes:
 *
 *   PLATFORM scope — "is_platform_admin":
 *     Issued by the backend when the JWT actor_scope_type == "platform".
 *     Reflected as `Me.is_platform_admin === true`.
 *     There is NO `platform:admin` permission string — the gate is ONLY the
 *     boolean field. The `system:admin` short-circuit does NOT apply here;
 *     a tenant-admin may hold `system:admin` but still has `is_platform_admin:false`.
 *
 *   TENANT scope — "permissions[]":
 *     The backend collapses roles → permissions before sending /me.
 *     `system:admin` is the tenant-side wildcard: a user holding it is treated
 *     as having every tenant-scoped permission (mirrors Unix root semantics).
 *     `system:admin` does NOT grant access to platform-scope surfaces.
 *
 * =============================================================================
 * CANONICAL PREDICATE
 * =============================================================================
 *
 * canSeeNavItem / canAccessRoute are the SINGLE source of truth for whether a
 * nav item or route is visible to a user. All other helpers (Sidebar, TopNav,
 * route guards) MUST delegate to these functions.
 *
 * Feature-flag gating is layered in the COMPONENT (flags need a hook). The
 * pure predicates stay flag-free. Use `navItemPasses` to AND an optional flag.
 *
 * Patterns:
 *   const can = useCan('blocks:approve')
 *   <RequirePermission permission="compliance:write">...</RequirePermission>
 *
 * Note on permission names: use names that are actually enforced by the backend
 * (defined in db/migrations/*.sql, granted to roles, checked by Go handlers).
 * The old `sars:approve` example was a phantom — SAR approval uses `compliance:write`.
 */

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

const SUPER_ADMIN = 'system:admin'

/** Returns true when `perms` contains the super-admin permission. */
function isSuperAdmin(perms: readonly string[] | undefined): boolean {
  return perms?.includes(SUPER_ADMIN) ?? false
}

// ---------------------------------------------------------------------------
// Pure functions (no hooks, take explicit perms/me arguments)
// ---------------------------------------------------------------------------

/**
 * Returns true if the user holds `permission`.
 * Honors the `system:admin` short-circuit.
 */
export function hasPermission(perms: readonly string[], permission: string): boolean {
  if (isSuperAdmin(perms)) return true
  return perms.includes(permission)
}

/**
 * Returns true if the user holds ANY of the listed permissions.
 * Honors the `system:admin` short-circuit.
 */
export function hasAnyPermission(perms: readonly string[], list: readonly string[]): boolean {
  if (isSuperAdmin(perms)) return true
  return list.some((p) => perms.includes(p))
}

/**
 * Returns true if the user holds ALL of the listed permissions.
 * Honors the `system:admin` short-circuit.
 */
export function hasAllPermissions(perms: readonly string[], list: readonly string[]): boolean {
  if (isSuperAdmin(perms)) return true
  return list.every((p) => perms.includes(p))
}

/**
 * Returns true when `me.is_platform_admin === true`.
 *
 * This is the ONLY way to gate platform-scope surfaces client-side.
 * The `system:admin` short-circuit intentionally does NOT apply.
 * Missing field (older tokens) is treated as false (fail-closed).
 */
export function isPlatformAdmin(me: Me | null | undefined): boolean {
  return me?.is_platform_admin === true
}

/**
 * Returns true when the user is a tenant-scope administrator.
 *
 * Tenant admin = NOT a platform admin AND holds tenant management permissions
 * (`tenant_users:admin` OR `tenant_settings:admin`).
 *
 * This is an informational hint. The backend remains the authoritative gate.
 */
export function isTenantAdmin(me: Me | null | undefined): boolean {
  if (!me) return false
  if (me.is_platform_admin) return false
  return (
    me.permissions.includes('tenant_users:admin') ||
    me.permissions.includes('tenant_settings:admin')
  )
}

/**
 * Returns true when the user is tenant-scoped (not a platform admin).
 *
 * All non-platform-admin users are tenant-scoped, including analysts,
 * compliance officers, and tenant admins.
 */
export function isTenantScopedUser(me: Me | null | undefined): boolean {
  if (!me) return false
  return me.is_platform_admin !== true
}

// ---------------------------------------------------------------------------
// Authorization context type
// ---------------------------------------------------------------------------

/**
 * Minimal context object used by the central predicates.
 * Derived from `Me` by the caller (Sidebar, ModuleTopNav, route guards).
 *
 * tenantViewId (Phase 3 — tenant-view switcher):
 *   When a platform admin has selected a tenant to view, this field holds
 *   the tenant's public_id. The Sidebar uses this to surface scope:'tenant'
 *   items alongside scope:'platform' items, so the platform admin can navigate
 *   tenant-operational surfaces (Alerts, Cases, Customers, etc.) while
 *   acting within that tenant's context.
 *
 *   For tenant-scoped users this field is always undefined (they never use
 *   the tenant-view switcher; their tenant is implicit in their JWT).
 *
 *   For platform admins in platform mode (no tenant selected) this is also
 *   undefined, and the sidebar shows only scope:'platform' items.
 */
export interface AuthContext {
  permissions: readonly string[]
  isPlatformAdmin: boolean
  /**
   * Set when a platform admin has selected a specific tenant to view.
   * Undefined means "no tenant selected" (platform mode or tenant user).
   */
  tenantViewId?: string | null
}

/**
 * Builds an AuthContext from a Me object. Safe to call with undefined/null.
 *
 * @param tenantViewId — optional: the public_id of the currently viewed tenant
 *   (Phase 3, tenant-view switcher). Pass this when building the context for a
 *   platform admin who has selected a tenant to view. Omit or pass null/undefined
 *   for normal operation.
 */
export function makeAuthContext(me: Me | null | undefined, tenantViewId?: string | null): AuthContext {
  return {
    permissions: me?.permissions ?? [],
    isPlatformAdmin: me?.is_platform_admin === true,
    tenantViewId: tenantViewId ?? undefined,
  }
}

// ---------------------------------------------------------------------------
// Central nav/route predicate
// ---------------------------------------------------------------------------

/**
 * THE CENTRAL PREDICATE — determines whether a nav item (or route) is visible
 * to the given user.
 *
 * Scope gate fires FIRST:
 *
 *   scope:'platform':
 *     Requires isPlatformAdmin. system:admin alone does NOT bypass the platform
 *     gate. A platform admin also checks declared permissions (W4 fix, ADR-0024):
 *     system:admin acts as the escape hatch so production platform admins see all
 *     platform items; a narrow-permission service account lacking both system:admin
 *     AND the specific perm will NOT see a permissioned platform item.
 *
 *   scope:'tenant':
 *     THREE sub-cases (Phase 4 W1 fix, ADR-0024):
 *
 *       (a) Platform admin in PLATFORM mode (isPlatformAdmin:true, tenantViewId absent):
 *           → FALSE always. Tenant-operational items (Alerts, Cases, Customers,
 *             Adverse Media, etc.) must be hidden from platform-mode admins even when
 *             they hold system:admin. This gate takes precedence over system:admin.
 *
 *       (b) Platform admin in TENANT-VIEW mode (isPlatformAdmin:true, tenantViewId set):
 *           → Permission-gated (system:admin short-circuit applies). The admin can
 *             navigate tenant surfaces while acting within a specific tenant's context.
 *
 *       (c) Tenant-scoped user (isPlatformAdmin:false):
 *           → Normal permission gate; system:admin wildcard applies.
 *
 *   scope:'both' (default when omitted):
 *     Permission-gated; system:admin short-circuit applies. Visible to both platform
 *     admins (any mode) and tenant users.
 *
 * Legacy compat: `platformAdminOnly: true` is treated identically to `scope:'platform'`.
 */
/**
 * Returns true when the platform admin is in tenant-view mode.
 *
 * Tenant-view mode: isPlatformAdmin === true AND tenantViewId is a non-empty
 * string. In this mode, scope:'tenant' items are surfaced alongside
 * scope:'platform' items so the admin can navigate both the platform-management
 * surfaces AND the tenant-operational surfaces (Alerts, Cases, Customers, etc.)
 * while acting within a specific tenant's context.
 */
function isInTenantViewMode(ctx: AuthContext): boolean {
  return ctx.isPlatformAdmin && typeof ctx.tenantViewId === 'string' && ctx.tenantViewId !== ''
}

export function canSeeNavItem(
  item: Pick<ModuleNavItem, 'permissions' | 'scope' | 'platformAdminOnly'>,
  ctx: AuthContext,
): boolean {
  const scope = item.scope ?? (item.platformAdminOnly ? 'platform' : 'both')

  // -------------------------------------------------------------------------
  // scope:'platform' — requires is_platform_admin. system:admin alone does NOT
  // bypass the platform scope gate. No tenant-view-mode distinction here:
  // platform items are always visible to platform admins regardless of mode.
  // -------------------------------------------------------------------------
  if (scope === 'platform') {
    if (!ctx.isPlatformAdmin) return false
    // Platform admin: also check declared permissions.
    // No permissions = visible to all platform admins.
    const perms = item.permissions
    if (!perms || perms.length === 0) return true
    // system:admin wildcard: platform admins holding system:admin see all permissioned items.
    if (isSuperAdmin(ctx.permissions)) return true
    return perms.some((p) => ctx.permissions.includes(p))
  }

  // -------------------------------------------------------------------------
  // scope:'tenant' — tenant-operational surfaces.
  //
  // Three sub-cases, in priority order:
  //
  //   1. Platform admin in TENANT-VIEW mode (isPlatformAdmin && tenantViewId set):
  //      → Surfaces tenant items alongside platform items. Run the permission
  //        sub-gate so narrow-permission service accounts don't see false
  //        affordances. In practice platform admins hold system:admin.
  //
  //   2. Platform admin in PLATFORM mode (isPlatformAdmin && tenantViewId absent):
  //      → MUST return false. Tenant-operational menus (Alerts, Cases, Customers,
  //        Adverse Media, etc.) are meaningless and potentially misleading without
  //        a tenant context. This gate takes precedence over the system:admin
  //        short-circuit — a platform admin with system:admin in platform mode
  //        must NOT see these items (Phase 4 W1 fix, ADR-0024).
  //
  //   3. Tenant-scoped user (isPlatformAdmin:false):
  //      → Normal permission gate; system:admin wildcard applies for tenant
  //        super-admins (mirrors Unix root semantics within the tenant).
  // -------------------------------------------------------------------------
  if (scope === 'tenant') {
    if (ctx.isPlatformAdmin) {
      // Sub-case 2: platform mode — hide tenant-operational items.
      if (!isInTenantViewMode(ctx)) return false
      // Sub-case 1: tenant-view mode — run permission gate.
      const perms = item.permissions
      if (!perms || perms.length === 0) return true
      if (isSuperAdmin(ctx.permissions)) return true
      return perms.some((p) => ctx.permissions.includes(p))
    }
    // Sub-case 3: tenant-scoped user — standard permission gate.
    const perms = item.permissions
    if (!perms || perms.length === 0) return true
    if (isSuperAdmin(ctx.permissions)) return true
    return perms.some((p) => ctx.permissions.includes(p))
  }

  // -------------------------------------------------------------------------
  // scope:'both' (default when scope is omitted) — visible to all user classes.
  // Permission gate still applies; system:admin short-circuit covers tenant admins.
  // Platform admins in either mode can see 'both' items (no platform-mode block).
  // -------------------------------------------------------------------------
  const perms = item.permissions
  if (!perms || perms.length === 0) return true

  // system:admin short-circuit (tenant-side wildcard; platform scope checked above)
  if (isSuperAdmin(ctx.permissions)) return true

  return perms.some((p) => ctx.permissions.includes(p))
}

/**
 * `canSeeNavItem` AND an optional feature-flag result.
 *
 * Use this in components where feature flags must be AND-ed with scope/permission:
 *   navItemPasses(item, ctx, featureFlagEnabled)
 *
 * If `item.featureFlag` is set but `flagEnabled` is undefined/false → hidden.
 * If `item.featureFlag` is absent → flag is ignored.
 */
export function navItemPasses(
  item: Pick<ModuleNavItem, 'permissions' | 'scope' | 'platformAdminOnly' | 'featureFlag'>,
  ctx: AuthContext,
  flagEnabled?: boolean,
): boolean {
  if (!canSeeNavItem(item, ctx)) return false
  if (item.featureFlag !== undefined) return flagEnabled === true
  return true
}

/**
 * Alias: same logic as canSeeNavItem, named for route-guard context.
 * Route meta uses the same shape as ModuleNavItem for the relevant fields.
 */
export function canAccessRoute(
  routeMeta: Pick<ModuleNavItem, 'permissions' | 'scope' | 'platformAdminOnly'>,
  ctx: AuthContext,
): boolean {
  return canSeeNavItem(routeMeta, ctx)
}

// ---------------------------------------------------------------------------
// Hook wrappers (React, read from /me)
// ---------------------------------------------------------------------------

export function useCan(permission: string): boolean {
  const { data: me } = useMe()
  if (!me) return false
  if (isSuperAdmin(me.permissions)) return true
  return me.permissions.includes(permission)
}

export function useCanAny(...permissions: readonly string[]): boolean {
  const { data: me } = useMe()
  if (!me) return false
  if (isSuperAdmin(me.permissions)) return true
  return permissions.some((p) => me.permissions.includes(p))
}

export function useCanAll(...permissions: readonly string[]): boolean {
  const { data: me } = useMe()
  if (!me) return false
  if (isSuperAdmin(me.permissions)) return true
  return permissions.every((p) => me.permissions.includes(p))
}

/**
 * Returns true only for platform-scope administrators.
 *
 * Reads `me.is_platform_admin` — the backend-issued boolean field on /me
 * (ADR-0023). This is the ONLY correct way to distinguish a platform-admin
 * from a tenant-admin: the backend sets `is_platform_admin: true` only for
 * users whose JWT scope is `actor_scope_type=platform`. There is no
 * `platform:admin` permission string on the real backend.
 *
 * The `system:admin` short-circuit intentionally does NOT apply here.
 * A tenant-admin may hold `system:admin` within their tenant but will have
 * `is_platform_admin: false` (or absent). Missing field is treated as false
 * for back-compat with older tokens that pre-date the field.
 *
 * Use this hook (or `RequirePlatformAdminRoute`) wherever the backend requires
 * `actor_scope_type=platform` — i.e. surfaces that were previously behind
 * `system:admin` alone but are now restricted to the platform scope.
 */
export function useIsPlatformAdmin(): boolean {
  const { data: me } = useMe()
  return me?.is_platform_admin === true
}

/**
 * Returns true when the current user is a tenant-scope administrator.
 * Hook wrapper around `isTenantAdmin`.
 */
export function useIsTenantAdmin(): boolean {
  const { data: me } = useMe()
  return isTenantAdmin(me)
}

/**
 * Returns true when the current user is NOT a platform administrator.
 * All non-platform-admin users (analysts, compliance officers, tenant admins)
 * are tenant-scoped.
 * Hook wrapper around `isTenantScopedUser`.
 */
export function useIsTenantScopedUser(): boolean {
  const { data: me } = useMe()
  return isTenantScopedUser(me)
}